import { Button } from 'blaze-ui/Button'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useMemo, useCallback, useState } from 'react'
import { useBrandPlanSchedules } from 'hooks/queries/schedules/useBrandPlanSchedules'
import { collectResults } from 'utils/query'
import dayjs from 'dayjs'
import { useModals } from 'almanac-ui/Modal'
import { IntegrationsModal } from 'components/IntegrationsModal/IntegrationsModal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useRootStore } from 'stores/RootStore'
import { IntegrationsDrawer } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/IntegrationsDrawer'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useConnectAccount } from 'hooks/useConnectAccount'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import X02 from 'blaze-ui/icons/16/X02'
import { IntegrationButton } from './IntegrationButton/IntegrationButton'
import { ProgressLoader } from './ProgressLoader/ProgressLoader'
import { INTEGRATION_PRIORITY_ORDER, getProviderForTactic, getIntegrationConfig } from './integrationConfig'
import Styles from './IntegrationsBanner.module.scss'

type ScheduleContentKey =
  | 'instagram_contents'
  | 'facebook_contents'
  | 'linkedin_contents'
  | 'twitter_contents'
  | 'email_contents'
  | 'seo_contents'
  | 'tiktok_contents'

// users created after this date will see blog integrations [BLZ-8308]
const BLOG_CONTENT_CUTOFF_DATE = '2025-09-22'

const INTEGRATION_CONTENT_MAP: ReadonlyArray<{ key: ScheduleContentKey; integration: string }> = [
  { key: 'instagram_contents', integration: 'Instagram' },
  { key: 'facebook_contents', integration: 'Facebook' },
  { key: 'linkedin_contents', integration: 'LinkedIn' },
  { key: 'twitter_contents', integration: 'Twitter' },
  { key: 'email_contents', integration: 'Email' },
  { key: 'seo_contents', integration: 'SEO' },
  { key: 'tiktok_contents', integration: 'TikTok' },
] as const

const DISMISSAL_KEY = 'integrations-banner-dismissed'

export const IntegrationsBanner = () => {
  const isIntegrationsBannerEnabled = useFlipperFlag('integrations_banner')
  const { data: brandPlan } = useCurrentBrandPlan()
  const { data: externalIntegrationsData } = useExternalIntegrations()
  const { openModal } = useModals()
  const { isMobile } = useResponsiveDesign()
  const rootStore = useRootStore()
  const {
    drawersStore: { openDrawer },
  } = rootStore
  const connectAccount = useConnectAccount()
  const auth = useAuth()
  const workspaceId = useCurrentWorkspaceId()
  const [localDismissed, setLocalDismissed] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  const dismissalKey = `${DISMISSAL_KEY}-${workspaceId}`
  const dismissed = localDismissed || !!localStorage.getItem(dismissalKey)

  const schedulesQuery = useBrandPlanSchedules(
    brandPlan?.id
      ? {
          brandPlanId: brandPlan.id,
          start_date: dayjs().format('YYYY-MM-DD'),
        }
      : { brandPlanId: -1, start_date: '' },
    { enabled: !!brandPlan?.id },
  )
  const schedules = collectResults(schedulesQuery, (data) => data.schedules)

  const shouldShowBlogContent = useMemo(() => {
    if (!auth?.created_at) return false
    return dayjs(auth.created_at).isAfter(dayjs(BLOG_CONTENT_CUTOFF_DATE))
  }, [auth?.created_at])

  const integrationsFromSchedules = useMemo(() => {
    if (schedules.length === 0) return []

    const integrations = schedules.flatMap((schedule) =>
      INTEGRATION_CONTENT_MAP.filter(({ key }) => {
        if (!shouldShowBlogContent && (key === 'email_contents' || key === 'seo_contents')) {
          return false
        }

        const contents = schedule[key] as Array<{ count: number }> | undefined
        return contents?.some((content) => content.count > 0)
      }).map(({ integration }) => integration),
    )

    return [...new Set(integrations)]
  }, [schedules, shouldShowBlogContent])

  const integrationStatus = useMemo(() => {
    if (!brandPlan || !externalIntegrationsData || integrationsFromSchedules.length === 0) {
      return { missingIntegrations: [], connectedCount: 0, totalCount: 0 }
    }

    const connectedProviders = new Set(
      externalIntegrationsData.external_integrations.map((integration) => integration.provider),
    )

    const implementedIntegrations = integrationsFromSchedules.filter(
      (integration) => getIntegrationConfig(integration, brandPlan.selected_blog_platform)?.isImplemented,
    )

    const isIntegrationConnected = (integration: string): boolean => {
      const requiredProvider = getProviderForTactic(integration, brandPlan.selected_blog_platform)
      return Boolean(requiredProvider && connectedProviders.has(requiredProvider))
    }

    const missingIntegrations = implementedIntegrations.filter((integration) => !isIntegrationConnected(integration))
    const totalCount = implementedIntegrations.length
    const connectedCount = totalCount - missingIntegrations.length

    return { missingIntegrations, connectedCount, totalCount }
  }, [brandPlan, externalIntegrationsData, integrationsFromSchedules])

  const { missingIntegrations, connectedCount, totalCount } = integrationStatus

  const openIntegrationModal = () => {
    if (isMobile) {
      openDrawer(IntegrationsDrawer)
    } else {
      openModal(IntegrationsModal, {})
    }
  }

  const nextIntegration = INTEGRATION_PRIORITY_ORDER.find((integration) => missingIntegrations.includes(integration))

  const progressPercentage = totalCount > 0 ? (connectedCount / totalCount) * 100 : 0

  const handleConnectIntegration = useCallback(
    (integration: string) => {
      const provider = getProviderForTactic(integration, brandPlan?.selected_blog_platform)
      if (provider) {
        connectAccount(provider)
      }
    },
    [connectAccount, brandPlan?.selected_blog_platform],
  )

  const handleDismiss = () => {
    setIsClosing(true)
    setTimeout(() => {
      localStorage.setItem(dismissalKey, 'true')
      setLocalDismissed(true)
    }, 300)
  }

  if (
    !isIntegrationsBannerEnabled ||
    dismissed ||
    !brandPlan ||
    integrationsFromSchedules.length === 0 ||
    missingIntegrations.length === 0 ||
    !nextIntegration ||
    isMobile
  ) {
    return null
  }

  return (
    <div className={`${Styles.banner} ${isClosing ? Styles.closing : ''}`}>
      <div className={Styles.progressSection}>
        <div className={Styles.circularProgress}>
          <ProgressLoader percentage={progressPercentage} size={28} />
        </div>
        <div className={Styles.progressText}>
          {connectedCount}/{totalCount} accounts connected!
        </div>
      </div>
      <div className={Styles.content}>
        <div className={Styles.nextUpSection}>
          <span className={Styles.nextUpText}>Next up</span>
          <IntegrationButton
            integration={nextIntegration}
            selectedBlogPlatform={brandPlan?.selected_blog_platform}
            onPress={() => handleConnectIntegration(nextIntegration)}
          />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button variant="tertiary" size="sm" onPress={openIntegrationModal} className={Styles.seeAllButton}>
          See All Integrations →
        </Button>
        <button onClick={handleDismiss} className={Styles.closeButton} aria-label="Dismiss banner">
          <X02 size={20} color="var(--dark-80)" />
        </button>
      </div>
    </div>
  )
}
